/**
 * useBootSequence — explicit state machine for MrPibb's startup sequence.
 *
 * Replaces the previous bag of independent useEffects (auth, metadata init,
 * fetchConversations, suggestions, auto-open) with one discriminated union
 * that represents "what is the user allowed to see right now." Only
 * `phase === 'ready'` renders the real chat UI; every other phase renders
 * the loading screen.
 *
 * This hook intentionally knows nothing about conversations, WebSockets, or
 * REST endpoints — it only tracks phase transitions, a per-phase stall
 * detector, and phase-timing telemetry. `App.tsx` (or whichever component
 * owns the boot sequence) drives the transitions by calling the resolver
 * functions once the corresponding work completes. This keeps the state
 * machine reusable by a future independently-deployed page that has a
 * different backend but the same "loading screen until ready" contract.
 *
 * No pacing: phases transition the instant the caller resolves them. The
 * stall-detector timeout only catches a phase that never resolves — it
 * never delays a phase that finishes early.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getBootPhaseStallMs } from './common/constants';

export type BootStep = 'authenticating' | 'loadingWorkspace' | 'openingConversation';

export type BootPhase =
  | { phase: 'authenticating' }
  | { phase: 'loadingWorkspace' }
  | { phase: 'openingConversation'; conversationId: string }
  | { phase: 'ready' }
  | { phase: 'error'; step: BootStep; message: string };

export interface UseBootSequenceReturn {
  bootPhase: BootPhase;
  /** Advance from 'authenticating' to 'loadingWorkspace'. */
  resolveAuthenticating: () => void;
  /**
   * Advance from 'loadingWorkspace'. Pass a conversationId to move into
   * 'openingConversation' (a most-recent conversation exists), or omit it
   * to go straight to 'ready' (no conversations — empty state).
   */
  resolveWorkspace: (conversationId?: string) => void;
  /** Advance from 'openingConversation' to 'ready'. */
  resolveOpeningConversation: () => void;
  /** Move the given step into the 'error' phase with a message to display. */
  failStep: (step: BootStep, message: string) => void;
  /** Retry the step that failed — re-enters that step's phase, not 'authenticating'. */
  retry: () => void;
}

/** Telemetry hook point — swap for a real metrics client if one exists later. */
function recordPhaseTiming(step: BootStep, durationMs: number): void {
  // eslint-disable-next-line no-console
  console.debug(`[boot] phase=${step} ms=${Math.round(durationMs)}`);
}

export function useBootSequence(): UseBootSequenceReturn {
  const [bootPhase, setBootPhase] = useState<BootPhase>({ phase: 'authenticating' });

  // Tracks when the current step started, for both the stall detector and telemetry.
  const stepStartedAt = useRef<number>(Date.now());
  // Remembers the phase to re-enter on retry (the failed step's starting phase).
  const retryTargetPhase = useRef<BootPhase>({ phase: 'authenticating' });
  // Lets the resolvers below guard against firing outside their expected
  // phase — e.g. onConversationLoaded fires on every conversationLoaded
  // frame, including ones long after boot, when a user switches
  // conversations from the sidebar post-'ready'. Without this guard each
  // resolver would spuriously re-enter its target phase and log bogus
  // telemetry for a step that isn't actually in progress.
  const bootPhaseRef = useRef<BootPhase>(bootPhase);
  bootPhaseRef.current = bootPhase;

  const enterPhase = useCallback((next: BootPhase) => {
    // Update synchronously, not just via the render-time sync line above —
    // that line only runs on re-render, which doesn't help if a resolver is
    // called twice within the same render cycle (e.g. two conversationLoaded
    // frames processed in the same microtask batch, before React commits).
    bootPhaseRef.current = next;
    stepStartedAt.current = Date.now();
    setBootPhase(next);
  }, []);

  const completeStep = useCallback((step: BootStep) => {
    recordPhaseTiming(step, Date.now() - stepStartedAt.current);
  }, []);

  const resolveAuthenticating = useCallback(() => {
    if (bootPhaseRef.current.phase !== 'authenticating') return;
    completeStep('authenticating');
    const next: BootPhase = { phase: 'loadingWorkspace' };
    retryTargetPhase.current = next;
    enterPhase(next);
  }, [completeStep, enterPhase]);

  const resolveWorkspace = useCallback((conversationId?: string) => {
    if (bootPhaseRef.current.phase !== 'loadingWorkspace') return;
    completeStep('loadingWorkspace');
    const next: BootPhase = conversationId
      ? { phase: 'openingConversation', conversationId }
      : { phase: 'ready' };
    retryTargetPhase.current = next;
    enterPhase(next);
  }, [completeStep, enterPhase]);

  const resolveOpeningConversation = useCallback(() => {
    if (bootPhaseRef.current.phase !== 'openingConversation') return;
    completeStep('openingConversation');
    enterPhase({ phase: 'ready' });
  }, [completeStep, enterPhase]);

  const failStep = useCallback((step: BootStep, message: string) => {
    enterPhase({ phase: 'error', step, message });
  }, [enterPhase]);

  const retry = useCallback(() => {
    enterPhase(retryTargetPhase.current);
  }, [enterPhase]);

  // Stall detector: if the current step hasn't resolved within the
  // per-stage threshold, surface an error instead of hanging indefinitely.
  useEffect(() => {
    if (bootPhase.phase === 'ready' || bootPhase.phase === 'error') return;

    const step = bootPhase.phase;
    const elapsed = Date.now() - stepStartedAt.current;
    const remaining = Math.max(getBootPhaseStallMs() - elapsed, 0);

    const timer = setTimeout(() => {
      failStep(step, `Taking longer than expected to finish "${step}".`);
    }, remaining);

    return () => clearTimeout(timer);
  }, [bootPhase, failStep]);

  return {
    bootPhase,
    resolveAuthenticating,
    resolveWorkspace,
    resolveOpeningConversation,
    failStep,
    retry,
  };
}
